import { AssignedEquipmentModel } from './../../../../models/equipmentsModel';
import { getUsersIncludingTerminated, getUsersList } from '../../../../core/store/users/users.actions';
import { SelectItem } from 'primeng/api';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { deepClone } from 'src/app/helpers/utils';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import {
  loadUserEquipments,
  saveReturnEquipment,
  changedReturnEquipment,
  loadManageAssetLocations
} from 'src/app/core/store/equipment/equipment.actions';
import { formatDate } from '@angular/common';
import { ManageAssetService } from 'src/app/core/services/manage-asset.service';
import { ManageAssetAssignmentSummaryModel, ManageAssetBarcodeLookupOptionModel, ManageAssetBarcodeLookupResultModel, ManageAssetCampusModel } from 'src/app/models/manageAssetModels';
@Component({
    selector: 'app-return-equipment',
    templateUrl: './return-equipment.component.html',
    styleUrls: ['./return-equipment.component.scss'],
    standalone: false
})
export class ReturnEquipmentComponent extends BaseComponent implements OnInit {
  readonly returnStatusOptions: SelectItem[] = [
    { label: 'Working', value: 1 },
    { label: 'Damaged', value: 2 },
    { label: 'Destroyed', value: 3 },
    { label: 'Not Returned', value: 4 }
  ];
  readonly assetItemConditionOptions = [
    { id: 1, assetItemCondition: 'New' },
    { id: 2, assetItemCondition: 'Excellent / Like New' },
    { id: 3, assetItemCondition: 'Good' },
    { id: 4, assetItemCondition: 'Fair / Average' },
    { id: 5, assetItemCondition: 'Poor' },
    { id: 6, assetItemCondition: 'Unserviceable / Damaged' },
    { id: 7, assetItemCondition: 'Obsolete / End of Life' }
  ];
  @Input() initialUserID: number = null;
  @Input() initialCampusID: number = null;
  @Input() initialAssetAssignedID: number = null;
  @Input() initialSelection: AssignedEquipmentModel = null;
  @Input() selectionKey = 0;

  canEdit: boolean;
  assignedEquipmentsToUser: AssignedEquipmentModel[];
  returningEquipments: AssignedEquipmentModel[];
  usersList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  returnCampusList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  locationList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  returnTargetType: 'user' | 'campus' = 'user';
  userID: number;
  assignedCampusID: number = null;
  showErrors = false;
  savingReturnEquipments = false;
  loadingUserEquipments = false;
  includeTerminated = false;
  barcode = '';
  batchBarcodes = '';
  batchLookupLoading = false;
  batchLookupError = '';
  batchRows: { barcode: string; count: number; matches: ManageAssetAssignmentSummaryModel[] }[] = [];
  barcodeLookupLoading = false;
  barcodeLookupResult: ManageAssetBarcodeLookupResultModel;
  barcodeLookupError: string;
  barcodeOnlyReturns: AssignedEquipmentModel[] = [];
  pendingSelectedAssetAssignedID: number = null;
  showReturnConfirmDialog = false;
  reassignTargetType: 'user' | 'campus' = 'user';
  reassignToUserID: number = null;
  reassignToCampusID: number = null;
  reassignReturnDateString: string = null;
  reassignNote: string = '';
  reassignCampusList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  reassignErrorMessage: string = null;
  private activeUsers = [];
  private usersIncludingTerminated = [];
  private barcodeLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  /** Reference counts: two p-selects per row can overlap show/hide when switching focus. */
  private readonly openAssignedReturnSelectCounts = new Map<string, number>();
  private readonly openBarcodeReturnSelectCounts = new Map<string, number>();

  constructor(
    private store: Store<State>,
    private manageAssetService: ManageAssetService,
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(getUsersList());
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3058); //equipment manager
      }),
      this.store.select(s => s.equipmentState.assignedEquipmentsToUser).subscribe(assignedEquipmentsToUser => {
        this.assignedEquipmentsToUser = deepClone(assignedEquipmentsToUser);
        this.applyQueuedBarcodeReturns();
        this.refreshReturningEquipments();
      }),
      this.store.select(s => s.equipmentState.savingReturnEquipments).subscribe(savingReturnEquipments => {
        this.savingReturnEquipments = savingReturnEquipments;
      }),
      this.store.select(s => s.equipmentState.loadingUserEquipments).subscribe(loadingUserEquipments => {
        this.loadingUserEquipments = !!loadingUserEquipments;
      }),
      this.store.select(s => s.usersState.usersList).subscribe(usersList => {
        this.activeUsers = usersList ?? [];
        this.updateUsersList();
        if (this.userID) {
          this.syncIncludeTerminatedForUser(this.userID);
        }
      }),
      this.store.select(s => s.usersState.usersListIncludingTerminated).subscribe(usersListIncludingTerminated => {
        this.usersIncludingTerminated = usersListIncludingTerminated ?? [];
        this.updateUsersList();
        if (this.userID) {
          this.syncIncludeTerminatedForUser(this.userID);
        }
      }),
      this.store.select(s => s.equipmentState.manageAssetLocations).subscribe(locations => {
        const mappedLocations = locations && locations.length > 0
          ? locations.map(location => ({ label: `${location.name} (${location.campusName})`, value: location.id }))
          : [{ label: 'loading...', value: undefined }];
        this.locationList = mappedLocations;
      })
    );

    this.store.dispatch(getUsersIncludingTerminated());
    this.store.dispatch(loadManageAssetLocations());
    this.manageAssetService.getAssignableCampuses().subscribe({
      next: (campuses: ManageAssetCampusModel[]) => {
        const mappedCampuses = (campuses ?? []).map(c => ({ label: c.campusName, value: c.id }));
        this.reassignCampusList = [...mappedCampuses];
        this.reassignCampusList.unshift({ label: '', value: null });
        this.applyReturnCampusList(mappedCampuses);
      },
      error: () => {
        this.reassignCampusList = [{ label: '', value: null }];
        this.applyReturnCampusList([]);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectionKey && !changes.selectionKey.firstChange) {
      this.applyInitialSelection();
      return;
    }

    if (changes.selectionKey?.firstChange && this.selectionKey) {
      this.applyInitialSelection();
    }
  }

  override ngOnDestroy() {
    if (this.barcodeLookupDebounceHandle) {
      clearTimeout(this.barcodeLookupDebounceHandle);
      this.barcodeLookupDebounceHandle = null;
    }

    super.ngOnDestroy();
  }

  requestSave() {
    if (this.disableReturnControls) {
      return;
    }

    const allReturns = [...this.returningEquipments, ...this.barcodeOnlyReturns];
    const hasInvalidReturns = allReturns.some(item =>
      !item.assetLocationID
      || !item.returnNotes || !item.returnNotes.trim()
      || this.isConditionSelectionInvalid(item));
    const reassignValidationError = this.getReassignValidationError(allReturns);
    this.reassignErrorMessage = reassignValidationError;

    if (allReturns.length > 0 && !hasInvalidReturns && !reassignValidationError) {
      this.showErrors = false;
      this.showReturnConfirmDialog = true;
    } else {
      this.showErrors = true;
    }
  }

  save() {
    if (this.disableReturnControls) {
      return;
    }

    const allReturns = [...this.returningEquipments, ...this.barcodeOnlyReturns];
    const hasInvalidReturns = allReturns.some(item =>
      !item.assetLocationID
      || !item.returnNotes || !item.returnNotes.trim()
      || this.isConditionSelectionInvalid(item));
    const reassignValidationError = this.getReassignValidationError(allReturns);
    this.reassignErrorMessage = reassignValidationError;

    if (allReturns.length < 1 || hasInvalidReturns || !!reassignValidationError) {
      this.showErrors = true;
      this.showReturnConfirmDialog = false;
      return;
    }

    const requestItems = allReturns.map(item => ({
      ...item,
      reassignToUserID: this.hasReassignTarget ? this.reassignToUserID ?? undefined : undefined,
      reassignToCampusID: this.hasReassignTarget ? this.reassignToCampusID ?? undefined : undefined,
      reassignReturnDate: this.hasReassignTarget && this.reassignReturnDateString ? new Date(this.reassignReturnDateString) : undefined,
      reassignNote: this.hasReassignTarget && this.reassignNote ? this.reassignNote : undefined
    }));
    this.store.dispatch(saveReturnEquipment({ returningEquipments: requestItems, isReassign: this.hasReassignTarget }));
    this.showReturnConfirmDialog = false;
  }

  cancelSave() {
    if (this.disableReturnControls) {
      return;
    }

    this.showReturnConfirmDialog = false;
  }

  includeTerminatedChange(event){
    this.includeTerminated = !!event?.checked;
    this.updateUsersList();
  }

  changedUser() {
    this.batchRows = [];
    this.batchLookupError = '';
    this.returnTargetType = 'user';
    this.assignedCampusID = null;
    if (!this.userID) {
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({ userID: this.userID }));
  }

  changedReturnTargetType() {
    this.batchRows = [];
    this.batchLookupError = '';
    if (this.returnTargetType === 'campus') {
      this.userID = null;
      this.includeTerminated = false;
    } else {
      this.assignedCampusID = null;
    }
    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
  }

  changedCampus() {
    this.batchRows = [];
    this.batchLookupError = '';
    this.returnTargetType = 'campus';
    this.userID = null;
    if (!this.assignedCampusID) {
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({ assignedCampusID: this.assignedCampusID }));
  }

  changedEquipment(assignedEquipment: AssignedEquipmentModel) {
    this.reassignErrorMessage = null;
    if (assignedEquipment?.returned) {
      this.autoSelectGroupedReturns(assignedEquipment);
      this.initializeReturnDefaults(assignedEquipment);
    }
    this.updateAllowedAssetItemConditions(assignedEquipment);
    this.refreshReturningEquipments();
    this.store.dispatch(changedReturnEquipment({ assignedEquipment }));
  }

  changedBarcodeOnlyReturn() {
    this.reassignErrorMessage = null;
    (this.barcodeOnlyReturns ?? []).forEach(item => this.updateAllowedAssetItemConditions(item));
    this.refreshReturningEquipments();
  }

  changedReassignTargetType() {
    this.reassignErrorMessage = null;
    if (this.reassignTargetType === 'user') {
      this.reassignToCampusID = null;
    } else {
      this.reassignToUserID = null;
    }
  }

  changedReassignTarget() {
    this.reassignErrorMessage = null;
  }

  getAllReturns() {
    return [...(this.returningEquipments ?? []), ...(this.barcodeOnlyReturns ?? [])];
  }

  getReturnAssigneeLabel(item: AssignedEquipmentModel) {
    return item.assignedToUserName || item.campusName || 'General Inventory';
  }

  getReturnDescription(item: AssignedEquipmentModel) {
    return item.description || item.equipment?.description || '-';
  }

  getLocationLabel(assetLocationID: number) {
    if (!assetLocationID) {
      return '-';
    }

    return this.locationList.find(x => x.value === assetLocationID)?.label ?? '-';
  }

  getReturnStatusLabelForItem(item: AssignedEquipmentModel) {
    return this.getReturnStatusLabel(item.assetReturnStatusID || 1);
  }

  getConditionLabelForItem(item: AssignedEquipmentModel) {
    if (!item?.assetItemConditionID) {
      return '-';
    }
    return this.assetItemConditionOptions.find(x => x.id === item.assetItemConditionID)?.assetItemCondition ?? '-';
  }

  getReassignAssigneeLabel(): string {
    if (!this.hasReassignTarget) {
      return '-';
    }

    if (this.reassignTargetType === 'campus') {
      const campus = (this.reassignCampusList ?? []).find(x => x.value === this.reassignToCampusID);
      return campus?.label || '-';
    }

    const user = (this.usersList ?? []).find(x => x.value === this.reassignToUserID);
    return user?.label || '-';
  }

  onBarcodeEnterKey(event: KeyboardEvent) {
    if (this.disableReturnControls || this.barcodeLookupLoading) {
      return;
    }
    event.preventDefault();
    this.lookupBarcode();
  }

  lookupScannedReturns() {
    if (this.batchLookupLoading) {
      return;
    }
    const codes = (this.batchBarcodes || '').split(/[\s,;]+/).filter(Boolean);
    if (!codes.length || codes.length > 100) {
      this.batchLookupError = 'Enter between 1 and 100 barcodes.';
      return;
    }
    if (this.returnTargetType === 'user' ? !this.userID : !this.assignedCampusID) {
      this.batchLookupError = 'Select the user or campus whose assets are being returned.';
      return;
    }
    const userID = this.returnTargetType === 'user' ? this.userID : null;
    const campusID = this.returnTargetType === 'campus' ? this.assignedCampusID : null;
    const submittedValue = this.batchBarcodes;
    this.batchLookupLoading = true;
    this.batchLookupError = '';
    this.batchRows = [];
    this.manageAssetService.getAssignedAssets({
      barcodes: codes,
      assignedToUserID: userID || undefined,
      assignedToCampusID: campusID || undefined,
      assignmentState: 'assigned'
    }).subscribe({
      next: response => {
        if (this.userID !== userID || this.assignedCampusID !== campusID || this.batchBarcodes !== submittedValue) {
          return;
        }
        const rows = response?.result ?? [];
        const uniqueCodes = [...new Set(codes.map(code => code.replace(/-/g, '').toUpperCase()))];
        this.batchRows = uniqueCodes.map(normalized => ({
          barcode: codes.find(code => code.replace(/-/g, '').toUpperCase() === normalized),
          count: codes.filter(code => code.replace(/-/g, '').toUpperCase() === normalized).length,
          matches: rows.filter(item => [item.barcode, item.assetGUID]
            .some(code => (code || '').replace(/-/g, '').toUpperCase() === normalized))
        }));
      },
      error: err => {
        this.batchLookupError = typeof err?.error === 'string' ? err.error : err?.error?.message || 'Unable to look up scanned returns.';
        this.batchLookupLoading = false;
      },
      complete: () => { this.batchLookupLoading = false; }
    });
  }

  queueScannedReturn(row: ManageAssetAssignmentSummaryModel) {
    this.barcodeLookupResult = {
      found: true,
      barcode: row.barcode,
      assetType: row.assetType,
      assetCategory: row.assetCategory,
      description: row.description,
      serialNumber: row.serialNumber,
      options: []
    };
    this.queueBarcodeReturn({
      assetAssignedID: row.assetAssignedID,
      assetInventoryID: row.assetInventoryID,
      assetItemID: row.assetItemID,
      isInventoryAssignment: row.isInventoryAssignment,
      assignedToUserID: row.assignedToUserID,
      assignedToUserName: row.assignedToUserName,
      actionLabel: row.assignedToUserName ? `Return from ${row.assignedToUserName}` : 'Return to General Inventory',
      suggestedAssetLocation: row.inventoryAssetLocation,
      suggestedAssetLocationID: row.inventoryAssetLocationID,
      requireItemCondition: row.requireItemCondition,
      assetItemConditionID: row.assetItemConditionID
    });
  }

  isScannedReturnQueued(row: ManageAssetAssignmentSummaryModel): boolean {
    return [...this.returningEquipments, ...this.barcodeOnlyReturns]
      .some(item => item.assetAssignedID === row.assetAssignedID);
  }

  onBarcodeInputChange() {
    if (this.disableReturnControls) {
      return;
    }

    if (this.barcodeLookupDebounceHandle) {
      clearTimeout(this.barcodeLookupDebounceHandle);
      this.barcodeLookupDebounceHandle = null;
    }

    const barcode = this.barcode?.trim();
    if (!barcode || barcode.length < 2) {
      this.barcodeLookupResult = null;
      this.barcodeLookupError = null;
      return;
    }

    this.barcodeLookupDebounceHandle = setTimeout(() => {
      this.lookupBarcode(true);
    }, 300);
  }

  lookupBarcode(suppressErrors = false) {
    const barcode = this.barcode?.trim();
    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;

    if (!barcode) {
      if (!suppressErrors) {
        this.barcodeLookupError = 'Barcode is required.';
      }
      return;
    }

    this.barcodeLookupLoading = true;
    this.manageAssetService.lookupBarcode({ barcode }).subscribe({
      next: (result) => {
        this.barcodeLookupResult = result;
        if (!result?.found && !suppressErrors) {
          this.barcodeLookupError = result?.errorMessage || 'No active asset assignment was found for this barcode.';
        }
      },
      error: (err) => {
        if (!suppressErrors) {
          this.barcodeLookupError = err?.error || 'Barcode lookup failed.';
        }
      },
      complete: () => {
        this.barcodeLookupLoading = false;
      }
    });
  }

  queueBarcodeReturn(option: ManageAssetBarcodeLookupOptionModel) {
    const pendingReturn = this.buildBarcodeReturn(option);

    if (option.assignedToUserID) {
      const loadedReturn = this.userID === option.assignedToUserID
        ? this.assignedEquipmentsToUser?.find(item => item.assetAssignedID === option.assetAssignedID)
        : null;
      if (loadedReturn) {
        loadedReturn.returned = true;
        this.initializeReturnDefaults(loadedReturn);
        loadedReturn.assetLocationID = loadedReturn.assetLocationID || pendingReturn.assetLocationID;
        this.updateAllowedAssetItemConditions(loadedReturn);
        this.refreshReturningEquipments();
        this.store.dispatch(changedReturnEquipment({ assignedEquipment: loadedReturn }));
      } else {
        this.upsertBarcodeOnlyReturn(pendingReturn);
        if (this.userID !== option.assignedToUserID) {
          this.userID = option.assignedToUserID;
          this.store.dispatch(loadUserEquipments({ userID: option.assignedToUserID }));
        }
      }
    } else {
      this.upsertBarcodeOnlyReturn(pendingReturn);
    }

    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;
    this.barcode = '';
    this.showErrors = false;
  }

  removeBarcodeOnlyReturn(item: AssignedEquipmentModel) {
    this.barcodeOnlyReturns = this.barcodeOnlyReturns.filter(x => x.assetAssignedID !== item.assetAssignedID);
    this.refreshReturningEquipments();
  }

  trackByFn(index, obj){
      return index;
  }

  private applyInitialSelection() {
    this.pendingSelectedAssetAssignedID = this.initialAssetAssignedID ?? null;
    this.showErrors = false;
    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;
    this.barcodeOnlyReturns = [];

    this.userID = this.initialUserID ?? null;
    this.assignedCampusID = this.initialCampusID ?? null;
    this.returnTargetType = this.assignedCampusID ? 'campus' : 'user';

    if (!this.userID && !this.assignedCampusID) {
      this.seedInitialSelectionWithoutUser();
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    if (this.userID) {
      this.syncIncludeTerminatedForUser(this.initialUserID);
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({
      userID: this.userID || undefined,
      assignedCampusID: this.assignedCampusID || undefined
    }));
  }

  get isDirectSelectionMode() {
    return !!this.initialAssetAssignedID || !!this.initialSelection?.assetAssignedID;
  }

  get disableReturnControls() {
    return this.loadingUserEquipments || this.savingReturnEquipments;
  }

  get hasReturnTargetSelection(): boolean {
    return this.returnTargetType === 'campus'
      ? !!this.assignedCampusID
      : !!this.userID;
  }

  get showNoAssetsToReturnMessage(): boolean {
    return this.hasReturnTargetSelection
      && !this.loadingUserEquipments
      && (this.assignedEquipmentsToUser?.length ?? 0) < 1;
  }

  get hasReassignTarget(): boolean {
    return (this.reassignToUserID ?? 0) > 0 || (this.reassignToCampusID ?? 0) > 0;
  }

  get submitActionLabel(): string {
    return this.hasReassignTarget ? 'Re-assign' : 'Return';
  }

  get partialReturnKitSummaries() {
    const selectedReturns = this.getAllReturns().filter(item => item.returned);
    const selectedGroups = new Map<string, { selectedCount: number; expectedCount: number; }>();

    selectedReturns.forEach(item => {
      const groupNames = this.getGroupNames(item);
      if (!groupNames.length) {
        return;
      }

      const expectedCount = item?.equipment?.groupMemberCount ?? 0;
      groupNames.forEach(groupName => {
        const current = selectedGroups.get(groupName) ?? { selectedCount: 0, expectedCount: 0 };
        current.selectedCount += 1;
        current.expectedCount = Math.max(current.expectedCount, expectedCount);
        selectedGroups.set(groupName, current);
      });
    });

    return Array.from(selectedGroups.entries())
      .map(([groupName, counts]) => ({
        groupName,
        selectedCount: counts.selectedCount,
        expectedCount: counts.expectedCount > 0 ? counts.expectedCount : counts.selectedCount,
      }))
      .filter(x => x.selectedCount < x.expectedCount)
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }

  get hasPartialReturnKits(): boolean {
    return this.partialReturnKitSummaries.length > 0;
  }

  private refreshReturningEquipments() {
    this.returningEquipments = (this.assignedEquipmentsToUser ?? []).filter(e => e.returned);

    [...this.returningEquipments, ...this.barcodeOnlyReturns].forEach(item => {
      this.initializeReturnDefaults(item);
      this.updateAllowedAssetItemConditions(item);
    });
  }

  private applyQueuedBarcodeReturns() {
    this.barcodeOnlyReturns = this.barcodeOnlyReturns.filter((queuedReturn) => {
      const match = (this.assignedEquipmentsToUser ?? []).find(item => item.assetAssignedID === queuedReturn.assetAssignedID);
      if (!match) {
        return true;
      }

      match.returned = true;
      match.assetLocationID = queuedReturn.assetLocationID || match.assetLocationID;
      match.assetReturnStatusID = queuedReturn.assetReturnStatusID || match.assetReturnStatusID;
      match.assetItemConditionID = queuedReturn.assetItemConditionID ?? match.assetItemConditionID;
      match.returnStatus = queuedReturn.returnStatus || match.returnStatus;
      match.returnNotes = queuedReturn.returnNotes ?? match.returnNotes;
      match.dateReturnedString = queuedReturn.dateReturnedString || match.dateReturnedString;
      this.updateAllowedAssetItemConditions(match);
      return false;
    });

    this.applyPendingSelectedReturn();
  }

  private applyPendingSelectedReturn() {
    if (!this.assignedEquipmentsToUser?.length || !this.pendingSelectedAssetAssignedID) {
      return;
    }

    const match = this.assignedEquipmentsToUser.find(item => item.assetAssignedID === this.pendingSelectedAssetAssignedID);
    if (!match) {
      return;
    }

    match.returned = true;
    this.initializeReturnDefaults(match);
    this.updateAllowedAssetItemConditions(match);
    this.pendingSelectedAssetAssignedID = null;
    this.refreshReturningEquipments();
  }

  private buildBarcodeReturn(option: ManageAssetBarcodeLookupOptionModel): AssignedEquipmentModel {
    return {
      id: option.assetAssignedID,
      assetAssignedID: option.assetAssignedID,
      equipmentID: option.assetAssignedID,
      assetInventoryID: option.assetInventoryID,
      assetItemID: option.assetItemID,
      isInventoryAssignment: option.isInventoryAssignment,
      userID: option.assignedToUserID,
      assignedToUserName: option.assignedToUserName,
      assetCategory: this.barcodeLookupResult?.assetCategory,
      description: this.barcodeLookupResult?.description,
      barcode: this.barcodeLookupResult?.barcode,
      inventoryAssetLocation: option.suggestedAssetLocation,
      assetLocationID: option.suggestedAssetLocationID,
      assetReturnStatusID: this.getDefaultReturnStatusForCondition(option.assetItemConditionID),
      requireItemCondition: option.requireItemCondition === true,
      assetItemConditionID: option.assetItemConditionID ?? null,
      originalAssetItemConditionID: option.assetItemConditionID ?? null,
      allowedAssetItemConditions: [],
      returnStatus: this.getReturnStatusLabel(this.getDefaultReturnStatusForCondition(option.assetItemConditionID)),
      returnNotes: '',
      returned: true,
      dateReturnedString: this.todayDateString(),
      actionLabel: option.actionLabel,
      equipment: {
        id: option.assetItemID ?? option.assetInventoryID,
        assetInventoryID: option.assetInventoryID,
        assetItemID: option.assetItemID,
        isInventoryAssignment: option.isInventoryAssignment,
        assetCategory: this.barcodeLookupResult?.assetCategory,
        description: this.barcodeLookupResult?.description,
        barcode: this.barcodeLookupResult?.barcode,
        inventoryAssetLocation: option.suggestedAssetLocation,
        serialNumber: this.barcodeLookupResult?.serialNumber ?? '',
        brand: '',
        model: '',
        serviceTag: '',
        expressServiceCode: '',
        type: this.barcodeLookupResult?.assetType ?? 'Asset',
        purchaseDate: null,
        assetTag: this.barcodeLookupResult?.barcode ?? '',
        label: [this.barcodeLookupResult?.assetType, this.barcodeLookupResult?.description, this.barcodeLookupResult?.barcode].filter(x => !!x).join(' - '),
        archived: false,
        department: '',
        pricing: '',
        purchaseVendor: '',
        purchaseLocation: '',
        storedLocation: option.suggestedAssetLocation ?? '',
        storedLocationID: option.suggestedAssetLocationID ?? 0,
        storedOther: '',
        paymentMethod: '',
        size: '',
        classCode: '',
        classification: '',
        classificationOther: '',
        mainCampusID: 0,
        mainCampusName: '',
        equipmentTypeID: 0
      }
    };
  }

  private seedInitialSelectionWithoutUser() {
    const selection = this.initialSelection ? deepClone(this.initialSelection) : null;
    if (!selection?.assetAssignedID) {
      return;
    }

    selection.returned = true;
    if (selection.originalAssetItemConditionID === undefined) {
      selection.originalAssetItemConditionID = selection.assetItemConditionID ?? null;
    }
    selection.assetReturnStatusID = selection.assetReturnStatusID || this.getDefaultReturnStatusForCondition(selection.assetItemConditionID);
    selection.allowedAssetItemConditions = selection.allowedAssetItemConditions ?? [];
    selection.returnStatus = selection.returnStatus || this.getReturnStatusLabel(selection.assetReturnStatusID);
    selection.returnNotes = selection.returnNotes ?? '';
    selection.dateReturnedString = selection.dateReturnedString || this.todayDateString();
    selection.actionLabel = selection.actionLabel
      || (selection.assignedToUserName
        ? `Return from ${selection.assignedToUserName}`
        : (selection.campusName ? `Return from ${selection.campusName}` : 'Return to General Inventory'));
    selection.equipment = selection.equipment || {
      id: selection.assetItemID ?? selection.assetInventoryID,
      assetInventoryID: selection.assetInventoryID,
      assetItemID: selection.assetItemID,
      isInventoryAssignment: selection.isInventoryAssignment,
      assetCategory: selection.assetCategory,
      description: selection.description,
      barcode: selection.barcode,
      inventoryAssetLocation: selection.inventoryAssetLocation,
      serialNumber: '',
      brand: '',
      model: '',
      serviceTag: '',
      expressServiceCode: '',
      type: 'Asset',
      purchaseDate: null,
      assetTag: selection.barcode ?? '',
      label: [selection.description, selection.barcode].filter(x => !!x).join(' - '),
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: selection.inventoryAssetLocation ?? '',
      storedLocationID: 0,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainCampusID: 0,
      mainCampusName: '',
      equipmentTypeID: 0
    };

    this.upsertBarcodeOnlyReturn(selection);
  }

  private upsertBarcodeOnlyReturn(item: AssignedEquipmentModel) {
    const existingIndex = this.barcodeOnlyReturns.findIndex(x => x.assetAssignedID === item.assetAssignedID);
    if (existingIndex >= 0) {
      return;
    }

    this.barcodeOnlyReturns.push(item);

    this.refreshReturningEquipments();
  }

  private todayDateString() {
    return formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
  }

  private autoSelectGroupedReturns(seed: AssignedEquipmentModel) {
    const groupNames = this.getGroupNames(seed);
    if (!groupNames.length || !(this.assignedEquipmentsToUser?.length > 0)) {
      return;
    }

    this.assignedEquipmentsToUser.forEach(item => {
      if (item.assetAssignedID === seed.assetAssignedID) {
        return;
      }
      if (!this.sharesAnyGroup(seed, item)) {
        return;
      }
      if (item.returned) {
        return;
      }

      item.returned = true;
      this.initializeReturnDefaults(item);
      this.updateAllowedAssetItemConditions(item);
      this.store.dispatch(changedReturnEquipment({ assignedEquipment: item }));
    });
  }

  private initializeReturnDefaults(item: AssignedEquipmentModel) {
    if (!item) {
      return;
    }
    if (item.originalAssetItemConditionID === undefined) {
      item.originalAssetItemConditionID = item.assetItemConditionID ?? null;
    }
    if (!item.dateReturnedString) {
      item.dateReturnedString = this.todayDateString();
    }
    if (!item.assetReturnStatusID) {
      item.assetReturnStatusID = this.getDefaultReturnStatusForCondition(item.assetItemConditionID);
    }
    item.returnStatus = this.getReturnStatusLabel(item.assetReturnStatusID);
  }

  private getGroupNames(item: AssignedEquipmentModel): string[] {
    return (item?.equipment?.assetGroups ?? [])
      .map(x => `${x ?? ''}`.trim())
      .filter(x => x.length > 0);
  }

  private sharesAnyGroup(a: AssignedEquipmentModel, b: AssignedEquipmentModel): boolean {
    const aGroups = this.getGroupNames(a);
    const bGroups = this.getGroupNames(b);
    if (!aGroups.length || !bGroups.length) {
      return false;
    }

    const bGroupSet = new Set(bGroups);
    return aGroups.some(group => bGroupSet.has(group));
  }

  private updateUsersList() {
    const source = this.includeTerminated ? this.usersIncludingTerminated : this.activeUsers;
    this.usersList = source && source.length > 0
      ? source.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u.id }))
      : [{ label: 'loading...', value: undefined }];
    this.usersList.unshift({ label: '', value: null });
  }

  private applyReturnCampusList(campuses: SelectItem[]) {
    // Keep a separate list for return target that mirrors campus options.
    // The existing reassign list remains unchanged.
    // We include the empty option for optional filtering semantics.
    this.returnCampusList = [{ label: '', value: null }, ...(campuses ?? [])];
  }

  private syncIncludeTerminatedForUser(userID: number) {
    const normalizedUserID = Number(userID);
    if (!Number.isFinite(normalizedUserID) || normalizedUserID <= 0) {
      return;
    }

    const existsInActive = this.activeUsers.some(u => u.id === normalizedUserID);
    if (existsInActive) {
      return;
    }

    const existsInIncludingTerminated = this.usersIncludingTerminated.some(u => u.id === normalizedUserID);
    if (existsInIncludingTerminated) {
      this.includeTerminated = true;
      this.updateUsersList();
    }
  }

  onAssignedReturnSelectShow(equipment: AssignedEquipmentModel) {
    const k = this.assignedReturnRowKey(equipment);
    this.openAssignedReturnSelectCounts.set(k, (this.openAssignedReturnSelectCounts.get(k) ?? 0) + 1);
  }

  onAssignedReturnSelectHide(equipment: AssignedEquipmentModel) {
    const k = this.assignedReturnRowKey(equipment);
    const next = (this.openAssignedReturnSelectCounts.get(k) ?? 1) - 1;
    if (next <= 0) {
      this.openAssignedReturnSelectCounts.delete(k);
    } else {
      this.openAssignedReturnSelectCounts.set(k, next);
    }
  }

  isAssignedReturnSelectOpen(equipment: AssignedEquipmentModel): boolean {
    return (this.openAssignedReturnSelectCounts.get(this.assignedReturnRowKey(equipment)) ?? 0) > 0;
  }

  onBarcodeReturnSelectShow(equipment: AssignedEquipmentModel) {
    const k = this.barcodeReturnRowKey(equipment);
    this.openBarcodeReturnSelectCounts.set(k, (this.openBarcodeReturnSelectCounts.get(k) ?? 0) + 1);
  }

  onBarcodeReturnSelectHide(equipment: AssignedEquipmentModel) {
    const k = this.barcodeReturnRowKey(equipment);
    const next = (this.openBarcodeReturnSelectCounts.get(k) ?? 1) - 1;
    if (next <= 0) {
      this.openBarcodeReturnSelectCounts.delete(k);
    } else {
      this.openBarcodeReturnSelectCounts.set(k, next);
    }
  }

  isBarcodeReturnSelectOpen(equipment: AssignedEquipmentModel): boolean {
    return (this.openBarcodeReturnSelectCounts.get(this.barcodeReturnRowKey(equipment)) ?? 0) > 0;
  }

  private assignedReturnRowKey(e: AssignedEquipmentModel): string {
    const id = e.assetAssignedID ?? e.id;
    if (id != null) {
      return `a:${id}`;
    }
    return `e:${e.equipmentID ?? 0}-${e.barcode ?? ''}-${e.description ?? ''}`;
  }

  private barcodeReturnRowKey(e: AssignedEquipmentModel): string {
    const id = e.assetAssignedID ?? e.id;
    if (id != null) {
      return `b:${id}`;
    }
    return `b:${e.barcode ?? ''}-${e.description ?? ''}`;
  }

  private getReturnStatusLabel(assetReturnStatusID: number) {
    return this.returnStatusOptions.find(option => option.value === assetReturnStatusID)?.label ?? 'Working';
  }

  private getAllowedConditionIdsForStatus(statusId?: number): number[] {
    if (statusId === 1) {
      return [1, 2, 3, 4, 7];
    }
    if (statusId === 2) {
      return [4, 5, 6];
    }
    if (statusId === 3) {
      return [6];
    }
    return [];
  }

  private isConditionRequired(item: AssignedEquipmentModel): boolean {
    return item?.requireItemCondition === true
      && this.getAllowedConditionIdsForStatus(item.assetReturnStatusID).length > 0;
  }

  private canKeepUnsetCondition(item: AssignedEquipmentModel): boolean {
    const originalConditionId = item?.originalAssetItemConditionID ?? null;
    return !item?.assetItemConditionID && !originalConditionId;
  }

  private isConditionSelectionInvalid(item: AssignedEquipmentModel): boolean {
    if (!this.isConditionRequired(item)) {
      return false;
    }

    if (!item.assetItemConditionID) {
      return !this.canKeepUnsetCondition(item);
    }

    return !(item.allowedAssetItemConditions ?? []).some(x => x.id === item.assetItemConditionID);
  }

  private getReassignValidationError(items: AssignedEquipmentModel[]): string | null {
    if (!this.hasReassignTarget) {
      return null;
    }

    const hasProblematicStatus = (items ?? []).some(item =>
      item?.assetReturnStatusID === 3 || item?.assetReturnStatusID === 4);
    if (hasProblematicStatus) {
      return 'Cannot re-assign items with Destroyed or Not Returned status.';
    }

    return null;
  }

  private getDefaultReturnStatusForCondition(conditionId?: number): number {
    const id = Number(conditionId);
    if (!Number.isFinite(id) || id <= 0) {
      return 1; // Working
    }

    // Prefer Damaged for Poor/Unserviceable so current condition stays selectable by default.
    if (id === 5 || id === 6) {
      return 2; // Damaged
    }

    return 1; // Working
  }

  private updateAllowedAssetItemConditions(item: AssignedEquipmentModel) {
    if (!item) {
      return;
    }

    const allowedIds = this.getAllowedConditionIdsForStatus(item.assetReturnStatusID);
    item.allowedAssetItemConditions = this.assetItemConditionOptions.filter(x => allowedIds.includes(x.id));

    if (allowedIds.length === 0) {
      item.assetItemConditionID = null;
      return;
    }

    if (this.canKeepUnsetCondition(item)) {
      return;
    }

    if (!item.assetItemConditionID || !allowedIds.includes(item.assetItemConditionID)) {
      item.assetItemConditionID = item.allowedAssetItemConditions.length > 0 ? item.allowedAssetItemConditions[0].id : null;
    }
  }
}

